import React, { useMemo } from 'react';
import Select from 'react-select';

const isGroupedOptions = options =>
  Array.isArray(options) && options.some(opt => Array.isArray(opt?.options));

const isLabelValueOptions = options =>
  Array.isArray(options) &&
  options.length > 0 &&
  typeof options[0] === 'object' &&
  options[0] !== null &&
  'label' in options[0] &&
  'value' in options[0] &&
  !Array.isArray(options[0].options);

const flattenOptions = options =>
  (options || []).flatMap(opt => (Array.isArray(opt?.options) ? opt.options : [opt]));

/**
 * A searchable dropdown built on react-select (single or multi).
 *
 * Props:
 *  - options      Flat items ({label,value} / labelKey+valueKey) or grouped
 *                 [{ label: 'Group', options: [{ label, value }] }]
 *  - value        Selected value (or value[] when isMulti)
 *  - onChange     (value | value[] | '') => void
 *  - isMulti      Multi-select mode
 *  - variant      'default' | 'sheet' (borderless cell look)
 */
const SearchableSelect = ({
  options = [],
  labelKey = 'label',
  valueKey = 'value',
  value,
  onChange,
  placeholder = 'Select...',
  isClearable = true,
  isDisabled = false,
  isMulti = false,
  size = 'md',
  variant = 'default',
  className = '',
  ...rest
}) => {
  const selectOptions = useMemo(() => {
    if (!options?.length) return [];
    if (isGroupedOptions(options)) return options;

    // Already { label, value } and caller is using default labelKey/valueKey
    if (
      labelKey === 'label' &&
      valueKey === 'value' &&
      isLabelValueOptions(options)
    ) {
      return options;
    }

    return options.map(opt =>
      typeof opt === 'object'
        ? { label: opt[labelKey], value: opt[valueKey] }
        : { label: opt, value: opt }
    );
  }, [options, labelKey, valueKey]);

  const flatOptions = useMemo(() => flattenOptions(selectOptions), [selectOptions]);

  const selectedOption = useMemo(() => {
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      return flatOptions.filter(opt => values.includes(opt.value));
    }
    return flatOptions.find(opt => opt.value === value) || null;
  }, [flatOptions, value, isMulti]);

  const isSheet = variant === 'sheet';
  const isSmall = size === 'sm' || isSheet;
  const minHeight = isSheet ? '100%' : isSmall ? '31px' : '38px';
  const fontSize = isSheet ? 'inherit' : isSmall ? '0.875rem' : '1rem';

  const handleChange = opt => {
    if (!onChange) return;
    if (isMulti) {
      onChange((opt || []).map(item => item.value));
      return;
    }
    onChange(opt ? opt.value : '');
  };

  return (
    <Select
      className={['dochub-searchable-select', isSheet ? 'is-sheet' : '', className]
        .filter(Boolean)
        .join(' ')}
      classNamePrefix="dochub-searchable-select"
      options={selectOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      styles={{
        container: base => ({
          ...base,
          width: '100%',
          height: isSheet ? '100%' : undefined,
          minWidth: 0
        }),
        control: (base, state) => ({
          ...base,
          minHeight,
          height: isSheet ? '100%' : undefined,
          fontSize,
          borderColor: isSheet ? 'transparent' : '#ced4da',
          backgroundColor: isSheet ? 'transparent' : base.backgroundColor,
          boxShadow: isSheet
            ? state.isFocused
              ? 'inset 0 0 0 1px #1a73e8'
              : 'none'
            : 'none',
          borderWidth: isSheet ? 0 : base.borderWidth,
          borderRadius: isSheet ? 0 : base.borderRadius,
          cursor: 'pointer',
          '&:hover': {
            borderColor: isSheet ? 'transparent' : '#86b7fe'
          }
        }),
        valueContainer: base => ({
          ...base,
          padding: isSheet ? '0 2px' : isSmall ? '0 8px' : '2px 8px',
          height: isSheet ? '100%' : undefined
        }),
        indicatorsContainer: base => ({
          ...base,
          height: isSheet ? '100%' : minHeight
        }),
        dropdownIndicator: base => ({
          ...base,
          padding: isSheet || isSmall ? '2px' : '8px'
        }),
        clearIndicator: base => ({
          ...base,
          padding: isSheet || isSmall ? '2px' : '8px'
        }),
        indicatorSeparator: base => (isSheet ? { display: 'none' } : base),
        menuPortal: base => ({
          ...base,
          zIndex: 10050
        }),
        menu: base => ({
          ...base,
          fontSize: isSheet ? '0.875rem' : fontSize
        }),
        option: (base, { isFocused, isSelected }) => ({
          ...base,
          fontSize: isSheet ? '0.875rem' : fontSize,
          backgroundColor: isSelected ? '#0d6efd' : isFocused ? '#f0f4ff' : 'white',
          color: isSelected ? 'white' : 'inherit',
          cursor: 'pointer'
        }),
        multiValue: base => ({
          ...base,
          fontSize: '0.75rem'
        }),
        groupHeading: base => ({
          ...base,
          fontWeight: 700,
          fontSize: '0.8rem',
          color: '#212529',
          textTransform: 'none'
        }),
        singleValue: base => ({
          ...base,
          fontSize,
          margin: 0
        }),
        placeholder: base => ({
          ...base,
          fontSize,
          color: isSheet ? 'rgba(0, 0, 0, 0.45)' : '#6c757d',
          fontStyle: isSheet ? 'italic' : base.fontStyle,
          margin: 0
        }),
        input: base => ({
          ...base,
          fontSize,
          margin: 0,
          padding: 0
        })
      }}
      {...rest}
    />
  );
};

export default SearchableSelect;
